/* src/Lib/Contexts/FeatureFlagsContext.tsx */
import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Alert } from '@mui/material';
import { featureFlags } from '../Utils/feature-flags';
import { useCurrentUser } from './CurrentUserContext';
import { PageLoading } from '../../Components/PageLoading/PageLoading';

// Common Types

export type FeatureFlag = {
  enabled: boolean;
};

export type FeatureFlags = {
  [key: string]: FeatureFlag;
  self_scheduled_deliveries: FeatureFlag;
  hide_audit_status_column: FeatureFlag;
  banking_update_request: FeatureFlag;
  new_purchase_order: FeatureFlag;
  po_revision: FeatureFlag;
  prop65: FeatureFlag;
  vendor_catalog: FeatureFlag;
  sample_tags: FeatureFlag;
  cancelled_po: FeatureFlag;
  baseline_cost: FeatureFlag;
  new_inspo: FeatureFlag;
  reorder_chat: FeatureFlag;
  generate_asn: FeatureFlag;
};

// Context

export type FeatureFlagsContextType = {
  flags: FeatureFlags;
};

const defaultFlags = {
  self_scheduled_deliveries: { enabled: false },
  hide_audit_status_column: { enabled: false },
  banking_update_request: { enabled: false },
  new_purchase_order: { enabled: false },
  po_revision: { enabled: false },
  prop65: { enabled: false },
  vendor_catalog: { enabled: false },
  sample_tags: { enabled: false },
  cancelled_po: { enabled: false },
  baseline_cost: { enabled: false },
  new_inspo: { enabled: false },
  reorder_chat: { enabled: false },
  generate_asn: { enabled: false },
};

export const FeatureFlagsContext = React.createContext<FeatureFlagsContextType>(
  {
    flags: defaultFlags,
  },
);

// Provider

export type FeatureFlagsProviderProps = {
  children?: React.ReactNode;
};

export function FeatureFlagsProvider({ children }: FeatureFlagsProviderProps) {
  const { currentUser } = useCurrentUser();

  const [state, setState] = useState<{
    flags?: FeatureFlags;
    loading: boolean;
    error?: Error;
  }>({
    flags: undefined,
    loading: true,
    error: undefined,
  });

  useEffect(() => {
    const fetchFlags = async () => {
      setState((prevState) => ({ ...prevState, loading: true }));
      try {
        const response = await featureFlags();
        setState({
          flags: {
            ...defaultFlags,
            ...response,
          },
          loading: false,
          error: undefined,
        });
      } catch (err: unknown) {
        console.error('Error while fetching feature flags', err);
        setState({
          loading: false,
          error: err instanceof Error ? err : new Error('Unknown error'),
        });
      }
    };
    fetchFlags();
    const id = setInterval(fetchFlags, 300000);
    return () => clearInterval(id);
  }, []);

  const flagsMemoized = useMemo(() => {
    if (currentUser && currentUser.feature_flag_access) {
      const flags = Object.keys(currentUser.feature_flag_access).reduce(
        (acc, flag) => {
          acc[flag] = {
            enabled: currentUser.feature_flag_access[flag] === 'enabled',
          };
          return acc;
        },
        {} as FeatureFlags,
      );
      if (state.flags) {
        return {
          flags: {
            ...state.flags,
            ...flags,
          },
        };
      }
      return { flags };
    }
    return state.flags ? { flags: state.flags } : null;
  }, [JSON.stringify(state.flags), currentUser]);

  if (state.loading && !flagsMemoized) {
    return <PageLoading />;
  }

  if (flagsMemoized) {
    return (
      <FeatureFlagsContext.Provider value={flagsMemoized}>
        {children}
      </FeatureFlagsContext.Provider>
    );
  }

  if (state.error) {
    return (
      <Alert severity="error">
        An error occurred while loading vendor portal feature flags:
        <br />
        {state.error.message}
      </Alert>
    );
  }

  return null;
}

FeatureFlagsProvider.propTypes = {
  children: PropTypes.node,
};
